import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getSitePictures, toggleSitePicture, uploadSitePicture } from "../../services/AppService";
import closeButton from "../../assets/images/close.png";
import { useTranslation } from "react-i18next";
import { resolveMediaUrl } from "../../utility/media";
import { BiTrash } from "react-icons/bi";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import UploadLoader from "../../components/UploadLoader";


const SitePicturesForm = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { workOrderId } = useParams();

    const [file, setFile] = useState(null);
    const [pictures, setPictures] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const fileInputRef = useRef(null);

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        action: null,
        payload: null
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: ""
    });

    const fetchPictures = async () => {
        const res = await getSitePictures(workOrderId);
        setPictures(res || []);
    };

    useEffect(() => {
        if (workOrderId) fetchPictures();
    }, [workOrderId]);

    const handleUpload = () => {
        if (!file) {
            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message: "Please select a file"
            });
            return;
        }

        setConfirmModal({
            open: true,
            action: "upload",
            payload: null
        });
    };


    const handleConfirm = async () => {
        const { action, payload } = confirmModal;
        setConfirmModal({ open: false, action: null, payload: null });

        try {
            if (action === "upload") {
                setIsUploading(true);
                setUploadProgress(0);
                await uploadSitePicture(workOrderId, file, (progress) => {
                    setUploadProgress(progress);
                });
                setFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }

                setMessageModal({
                    open: true,
                    type: "success",
                    title: "Success",
                    message: "Image uploaded successfully"
                });
            }

            if (action === "delete") {
                await toggleSitePicture(payload);

                setMessageModal({
                    open: true,
                    type: "success",
                    title: "Success",
                    message: "Image deleted successfully"
                });
            }

            fetchPictures();
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong";

            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    };


    const handleDelete = (id) => {
        setConfirmModal({
            open: true,
            action: "delete",
            payload: id
        });
    };



    return (
        <div className="bg-white p-4 rounded shadow-md">

            {/* Upload Section */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-lg md:text-xl font-semibold text-blue uppercase">
                    {"Add / Edit Site Pictures"}
                </h4>
                <img src={closeButton} alt="Close" className="w-6 h-6 cursor-pointer" onClick={() => navigate(-1)} />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-center mb-4">
                <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*,image/jpeg,image/png,image/jpg"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="border p-2 rounded w-full sm:w-auto bg-white"
                />
                <button onClick={handleUpload}
                    disabled={!file}
                    className="staff-btn-save disabled:opacity-50"
                >
                    Add
                </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full border">
                    <thead className="bg-[#5f6db3] text-white">
                        <tr>
                            <th className="p-2 border">Sr</th>
                            <th className="p-2 border">Site Picture</th>
                            <th className="p-2 border">Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pictures.map((pic, idx) => (
                            <tr key={pic.id} className="text-center">
                                <td className="border p-2">{idx + 1}</td>

                                <td className="border p-2">
                                    <img
                                        src={resolveMediaUrl(pic.fileLocation)}
                                        alt="site"
                                        className="h-45 mx-auto object-contain"
                                    />
                                </td>

                                <td className="border p-2">
                                    <button
                                        onClick={() => handleDelete(pic.id)}
                                        className="text-red-600 text-xl"
                                    >
                                        <BiTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <ConfirmActionModal
                open={confirmModal.open}
                onClose={() => setConfirmModal({ open: false })}
                onConfirm={handleConfirm}
                title={
                    confirmModal.action === "upload"
                        ? "Confirm Upload"
                        : "Confirm Delete"
                }
                message={
                    confirmModal.action === "upload"
                        ? "Are you sure you want to upload this image?"
                        : "Are you sure you want to delete this image?"
                }
            />

            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />

            {isUploading && (
                <UploadLoader
                    text="Uploading site picture, please wait..."
                    progress={uploadProgress}
                />
            )}
        </div>
    );
};

export default SitePicturesForm;